import { useState, useEffect } from 'react';
import { Wallet, CreditCard, ChevronDown } from 'lucide-react';

export default function HeroSection() {
  const [timeLeft, setTimeLeft] = useState({
    days: 5,
    hours: 12,
    minutes: 34,
    seconds: 56
  });
  
  useEffect(() => {
    // Set end date to 5 days, 12 hours, 34 minutes and 56 seconds from now
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + 5);
    endDate.setHours(endDate.getHours() + 12);
    endDate.setMinutes(endDate.getMinutes() + 34);
    endDate.setSeconds(endDate.getSeconds() + 56);
    
    const timer = setInterval(() => {
      const now = new Date();
      const difference = endDate.getTime() - now.getTime();
      
      if (difference <= 0) {
        // Timer completed
        clearInterval(timer);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      } else {
        // Calculate remaining time
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        
        setTimeLeft({ days, hours, minutes, seconds });
      }
    }, 1000);
    
    // Clean up interval on component unmount
    return () => clearInterval(timer);
  }, []);
  
  const scrollToNextSection = () => {
    const nextSection = document.getElementById('presale');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  return (
    <section id="home" className="relative pt-24 pb-8 flex items-center overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-1/4 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-[100px] animate-pulse-slow"></div>
      <div className="absolute bottom-1/4 right-10 w-72 h-72 bg-purple-500/10 rounded-full blur-[100px] animate-pulse-slow"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <div className="glass-card p-8 lg:p-10 overflow-hidden relative">
              {/* Shine effect */}
              <div className="absolute -inset-full h-full w-1/2 z-0 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12 transform -translate-x-full animate-[shine_3s_infinite]"></div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-3">
                <span className="block gradient-heading" data-i18n="hero.title">The QuokkaX</span>
                <span className="block gradient-heading mt-1" data-i18n="hero.subtitle">revolution has begun!</span>
              </h1>
              
              <p className="text-xl card-text mb-4 max-w-lg" data-i18n="hero.description">
                Participate in the exclusive presale of the most futuristic memecoin in the crypto universe.
              </p>
              
              {/* Presale Countdown */}
              <div className="mb-4">
                <p className="text-sm card-text uppercase tracking-wider mb-3 font-medium" data-i18n="hero.countdown">Presale ends in:</p>
                <div className="grid grid-cols-4 gap-3">
                  <div className="glass-card px-2 py-4 text-center">
                    <div className="text-3xl lg:text-4xl font-mono gradient-text font-bold">{String(timeLeft.days).padStart(2, '0')}</div>
                    <div className="text-xs card-text uppercase tracking-wider mt-1" data-i18n="hero.days">Days</div>
                  </div>
                  <div className="glass-card px-2 py-4 text-center">
                    <div className="text-3xl lg:text-4xl font-mono gradient-text font-bold">{String(timeLeft.hours).padStart(2, '0')}</div>
                    <div className="text-xs card-text uppercase tracking-wider mt-1" data-i18n="hero.hours">Hours</div>
                  </div>
                  <div className="glass-card px-2 py-4 text-center">
                    <div className="text-3xl lg:text-4xl font-mono gradient-text font-bold">{String(timeLeft.minutes).padStart(2, '0')}</div>
                    <div className="text-xs card-text uppercase tracking-wider mt-1" data-i18n="hero.minutes">Minutes</div>
                  </div>
                  <div className="glass-card px-2 py-4 text-center">
                    <div className="text-3xl lg:text-4xl font-mono gradient-text font-bold">{String(timeLeft.seconds).padStart(2, '0')}</div>
                    <div className="text-xs card-text uppercase tracking-wider mt-1" data-i18n="hero.seconds">Seconds</div>
                  </div>
                </div>
              </div>
              
              {/* CTA Buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button className="gradient-button py-4 px-6 text-white font-medium">
                  <Wallet className="mr-2 h-5 w-5" />
                  <span data-i18n="nav.connect_wallet">Connect Wallet</span>
                </button>
                <button className="glass-card hover:bg-primary/10 border border-blue-500/10 text-primary py-4 px-6 rounded-full font-medium transition-all hover:shadow-blue flex items-center justify-center">
                  <CreditCard className="mr-2 h-5 w-5" />
                  <span data-i18n="nav.buy_with_card">Buy with Card</span>
                </button>
              </div>
            </div>
          </div>
          
          <div className="flex justify-center order-1 lg:order-2">
            <div className="relative">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/30 to-purple-500/30 rounded-full blur-[60px] animate-pulse-slow scale-90"></div>
              
              {/* Main image */}
              <img 
                src={`quokka.png?v=${Date.now()}`}
                alt="QuokkaX Robot Mascot" 
                className="relative z-10 w-full max-w-md animate-float drop-shadow-blue"
              />
              
              {/* Floating elements */}
              <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                <div className="absolute top-1/4 right-0 w-12 h-12 bg-blue-500/30 rounded-full animate-float blur-sm"></div>
                <div className="absolute bottom-1/3 left-0 w-8 h-8 bg-purple-500/30 rounded-full animate-float blur-sm" style={{ animationDelay: '1s' }}></div>
                <div className="absolute top-10 left-1/4 w-4 h-4 bg-white/30 rounded-full animate-float blur-sm" style={{ animationDelay: '1.5s' }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
} 